import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function BulkImagesPage() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]); // { file, name, previewUrl, productId, hadImage }
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [matchMode, setMatchMode] = useState('sku'); // 'sku' | 'name'
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/api/products').then(r => setProducts(r.data.products)).catch(() => {});
  }, []);

  const bySku = useMemo(() => {
    const m = {};
    products.forEach(p => { if (p.sku) m[p.sku.toLowerCase()] = p; });
    return m;
  }, [products]);

  // Normalize for item-name matching: strip extension, lowercase, collapse _ - and spaces.
  const norm = (s) => (s || '').replace(/\.[^.]+$/, '').toLowerCase().replace(/[_\-\s]+/g, ' ').trim();
  const byName = useMemo(
    () => products.map(p => ({ p, n: norm(p.name) })).filter(x => x.n),
    [products]
  );

  // Returns the matched product for a filename, per the current mode. Item-name
  // mode: exact first, else substring; prefer the longest (most specific) name.
  // Ambiguous (tie) → no auto-match so the user picks it manually.
  const matchFile = (filename) => {
    if (matchMode === 'sku') {
      const base = filename.replace(/\.[^.]+$/, '').toLowerCase();
      return bySku[base] || null;
    }
    const base = norm(filename);
    const exact = byName.filter(x => x.n === base);
    if (exact.length === 1) return exact[0].p;
    if (exact.length > 1) return null;
    const subs = byName.filter(x => base.includes(x.n) || x.n.includes(base));
    if (subs.length === 0) return null;
    subs.sort((a, b) => b.n.length - a.n.length);
    if (subs.length > 1 && subs[0].n.length === subs[1].n.length) return null;
    return subs[0].p;
  };

  const onFiles = (fileList) => {
    const files = Array.from(fileList).filter(f => /^image\//.test(f.type));
    const next = files.map(file => {
      const match = matchFile(file.name);
      return {
        file, name: file.name, previewUrl: URL.createObjectURL(file),
        productId: match?.id || null, hadImage: !!match?.image_url,
      };
    });
    setRows(next); setResult(null); setProgress(0);
  };

  // Re-run auto-matching when the mode changes (keeps the loaded files).
  useEffect(() => {
    setRows(prev => prev.map(r => {
      const match = matchFile(r.name);
      return { ...r, productId: match?.id || null, hadImage: !!match?.image_url };
    }));
    setResult(null);
  }, [matchMode]);

  const statusOf = (r) => {
    if (!r.productId) return 'no match';
    if (r.hadImage && !replaceExisting) return 'skip (has image)';
    return r.hadImage ? 'replace' : 'link';
  };
  const willUpload = rows.filter(r => r.productId && !(r.hadImage && !replaceExisting));

  const setRowProduct = (idx, productId) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const p = products.find(x => x.id === parseInt(productId));
      return { ...r, productId: p?.id || null, hadImage: !!p?.image_url };
    }));
  };

  const upload = async () => {
    if (!willUpload.length) return toast.error('Nothing to upload');
    setUploading(true); setProgress(0);
    let ok = 0, fail = 0;
    for (let i = 0; i < willUpload.length; i++) {
      const r = willUpload[i];
      try {
        const fd = new FormData();
        fd.append('image', r.file);
        await api.patch(`/api/products/${r.productId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch { fail++; }
      setProgress(i + 1);
    }
    const skipped = rows.length - willUpload.length;
    setResult({ ok, fail, skipped, unmatched: rows.filter(r => !r.productId).length });
    setUploading(false);
    toast.success(`Uploaded ${ok} image${ok === 1 ? '' : 's'}`);
    api.get('/api/products').then(res => setProducts(res.data.products)).catch(() => {});
  };

  const matchedCount = rows.filter(r => r.productId).length;

  return (
    <>
      <div className="topbar">
        <h2>Bulk Image Upload</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => nav('/admin/products')}>← Item Manager</button>
      </div>
      <div className="page">
        <div className="card" style={{ maxWidth: 900 }}>
          <p className="form-hint" style={{ marginTop: 0 }}>
            {matchMode === 'sku'
              ? <>Name each image file after the item's <strong>SKU</strong> (e.g. <code>TKT-1000.jpg</code>) and drop them all in.</>
              : <>Name each image file after the item's <strong>name</strong> (e.g. <code>Towne Health 4 Part.jpg</code>) and drop them all in. The most specific name wins; ties are left for you to pick.</>}
            {' '}Matching ignores case and file extension. Review below, then upload matched images in one click.
          </p>

          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="form-label">Match files to items by</label>
            <select className="form-select" value={matchMode} onChange={e => setMatchMode(e.target.value)}>
              <option value="sku">SKU in filename</option>
              <option value="name">Item name in filename</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              Select images
              <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => onFiles(e.target.files)} />
            </label>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              Select a folder
              <input type="file" accept="image/*" multiple webkitdirectory="" directory="" style={{ display: 'none' }} onChange={e => onFiles(e.target.files)} />
            </label>
            {rows.length > 0 && (
              <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
                <input type="checkbox" checked={replaceExisting} onChange={e => setReplaceExisting(e.target.checked)} />
                Replace images on items that already have one
              </label>
            )}
          </div>

          {rows.length > 0 && (
            <>
              <div style={{ margin: '12px 0', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span className="badge badge-active">{matchedCount} matched</span>
                {rows.length - matchedCount > 0 && <span className="badge badge-inactive">{rows.length - matchedCount} no match</span>}
                <span className="badge">{willUpload.length} will upload</span>
              </div>

              <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                <table style={{ fontSize: 13 }}>
                  <thead><tr><th></th><th>File</th><th>Matched item</th><th>Status</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td><img src={r.previewUrl} alt="" style={{ width: 38, height: 38, borderRadius: 5, objectFit: 'cover' }} /></td>
                        <td style={{ fontFamily: 'monospace' }}>{r.name}</td>
                        <td>
                          {r.productId
                            ? (products.find(p => p.id === r.productId)?.name || '')
                            : (
                              <select className="form-select" style={{ fontSize: 12, padding: '2px 6px' }}
                                value="" onChange={e => setRowProduct(i, e.target.value)}>
                                <option value="">— pick item —</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                              </select>
                            )}
                        </td>
                        <td style={{ color: statusOf(r).startsWith('no match') ? 'var(--brand-red)' : statusOf(r).startsWith('skip') ? 'var(--gray-500)' : 'var(--brand-green)' }}>
                          {statusOf(r)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 14 }}>
                <button className="btn btn-primary" onClick={upload} disabled={uploading || !willUpload.length}>
                  {uploading ? `Uploading… ${progress}/${willUpload.length}` : `Upload ${willUpload.length} matched image${willUpload.length === 1 ? '' : 's'}`}
                </button>
                {result && (
                  <span className="text-sm">
                    Done — <strong>{result.ok}</strong> uploaded{result.fail ? `, ${result.fail} failed` : ''}
                    {result.skipped ? `, ${result.skipped} skipped` : ''}{result.unmatched ? `, ${result.unmatched} unmatched` : ''}.
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
